import { useEffect, useState } from 'react'

import { access } from '@driftt/traffic-control'

import { fetchConversationSections, getNotificationsBySection } from '../api'
import {
  ConversationSidebarFilterCondition,
  ConversationSidebarNumericFilter,
  ConversationSidebarSectionConfig} from '../typings/conversations'
import { createConditionsArray,specifiedArraySort } from '../utils'
import { captureException } from '../utils/sentry'

export const QUEUE_TIME_PROPERTY = 'unresponded-time-since-routed'

export const hasQueueTimeCondition = (conditions: ConversationSidebarFilterCondition[]) =>
  !!conditions.find(c => {
    const cond = c.condition as ConversationSidebarNumericFilter
    return cond.name === QUEUE_TIME_PROPERTY
  })

export const getFilteredConversationSections = (sections: any, hasQueueTimeFilter?: boolean) => {
  const order = ['my-convos', 'no-participants', 'all-convos']
  const list = Object.values(sections)

  return specifiedArraySort(list, order, 'type').filter((section: any) => {
    const isVisible = section.visible ?? true
    if (!isVisible) {
      return false
    }
    if (section.type === 'inbox') {
      return false
    }
    const conditions = createConditionsArray(section.condition)
    if (!hasQueueTimeFilter && conditions) {
      return !hasQueueTimeCondition(conditions)
    }

    return true
  }) as ConversationSidebarSectionConfig[]
}

const getSubscriptionBySection = (
  subscriptions: Record<string, any>,
  sections: ConversationSidebarSectionConfig[],
  hasQueueTimeFilter: boolean
) => {
  const notificationSubscriptions: Record<string, any> = {}
  const filteredSections = getFilteredConversationSections(sections, hasQueueTimeFilter)

  filteredSections.forEach(sectionValue => {
    const { subscriptionId, type, title } = sectionValue
    const exists = subscriptions[subscriptionId ?? 0]
    notificationSubscriptions[subscriptionId ?? 0] = {
      allNotificationsForSubscriptionEnabled:
        exists?.allNotificationsForSubscriptionEnabled || false,
      subscriptionId,
      type,
      title
    }
  })

  return notificationSubscriptions
}

const getSubscriptionsValues = (notificationSubscriptions: Record<string, any>) => {
  const initialValues: Record<string, any> = {}
  Object.entries(notificationSubscriptions).forEach(
    ([subscriptionId, { allNotificationsForSubscriptionEnabled }]) => {
      initialValues[subscriptionId] = allNotificationsForSubscriptionEnabled
    }
  )
  return initialValues
}

export default function useSubscriptionsBySections() {
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(false)
  const [notificationSubscriptions, setNotificationSubscriptions] = useState<Record<string, any>>(
    {}
  )
  const [subscriptionValues, setSubscriptionValues] = useState<Record<string, any>>({})
  const [subscriptionsFailed, setSubscriptionsFailed] = useState(false)
  const hasQueueTimeFilter = access.hasGates('has_convo_queue_time_filter')

  const fetchSectionSubscriptions = async () => {
    setLoadingSubscriptions(true)
    setSubscriptionsFailed(false)

    try {
      const sections = await fetchConversationSections()
      const notificationSettings = await getNotificationsBySection()

      const formattedSubscriptions = getSubscriptionBySection(
        notificationSettings,
        sections,
        hasQueueTimeFilter
      )
      const subscriptionBySection = getSubscriptionsValues(formattedSubscriptions)

      setNotificationSubscriptions(formattedSubscriptions)
      setSubscriptionValues(subscriptionBySection)
    } catch (error) {
      setSubscriptionsFailed(true)
      captureException(error, { tags: { location: 'fetchSubscriptions-notificationSettings' } })
    } finally {
      setLoadingSubscriptions(false)
    }
  }

  const retrySectionsFetch = () => {
		setSubscriptionsFailed(true)
		fetchSectionSubscriptions()
  }

  useEffect(() => {
    fetchSectionSubscriptions()
  }, []) // eslint-disable-line

  return {
    loadingSectionNotificationPreferences: loadingSubscriptions,
    sectionNotificationPreferencesFailed: subscriptionsFailed,
    notificationSubscriptions,
    subscriptionValues,
    retrySectionsFetch
  }
}
